import { z } from 'zod';
import { listSearchSchema } from '@/utils/listSearch';
import {
  WEB_URL_DESCRIPTION_MAX_LENGTH,
  WEB_URL_LINK_MAX_LENGTH,
  WEB_URL_TITLE_MAX_LENGTH,
} from '../constants/webUrl.constants';

export const webUrlSchema = z.object({
  id: z.coerce.number(),
  title: z.string().nullable(),
  description: z.string().nullable(),
  actionLink: z.string().nullable(),
  activeFrom: z.coerce.date().nullable(),
  activeTo: z.coerce.date().nullable(),
  isActive: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const webUrlListSchema = z.array(webUrlSchema);

export type WebUrl = z.infer<typeof webUrlSchema>;

export const webUrlFormSchema = z
  .object({
    title: z
      .string()
      .trim()
      .min(1, 'Title is required')
      .max(
        WEB_URL_TITLE_MAX_LENGTH,
        `Title must be ${WEB_URL_TITLE_MAX_LENGTH} characters or fewer`,
      ),
    description: z
      .string()
      .trim()
      .max(
        WEB_URL_DESCRIPTION_MAX_LENGTH,
        `Description must be ${WEB_URL_DESCRIPTION_MAX_LENGTH} characters or fewer`,
      ),
    actionLink: z
      .string()
      .trim()
      .min(1, 'Link is required')
      .max(WEB_URL_LINK_MAX_LENGTH, `Link must be ${WEB_URL_LINK_MAX_LENGTH} characters or fewer`)
      .refine((value) => /^https?:\/\/.+/i.test(value), 'Must start with http:// or https://'),
    activeFrom: z.string().min(1, 'Active From is required'),
    activeTo: z.string().min(1, 'Active To is required'),
    isActive: z.boolean(),
  })
  .refine(({ activeFrom, activeTo }) => activeTo === '' || activeFrom <= activeTo, {
    path: ['activeTo'],
    message: 'Active To cannot be before Active From',
  });

export type WebUrlFormValues = z.infer<typeof webUrlFormSchema>;

export interface CreateWebUrlPayload {
  title: string;
  actionLink: string;
  activeFrom: string;
  activeTo: string;
  description?: string;
  isActive?: boolean;
}

export type UpdateWebUrlPayload = Partial<CreateWebUrlPayload>;

export interface WebUrlListParams {
  activeFrom?: string | undefined;
  activeTo?: string | undefined;
}

export const webUrlSearchSchema = z.object({
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  ...listSearchSchema.shape,
});
